import { type AuditItem, type EmployeePhoto, type FileItem, PHOTO_MAX_SIZE, PHOTO_MIMES } from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useRef, useState } from 'react';
import { Comments } from '@/components/comments';
import { Avatar, Badge, Button, Dl, ErrorBox, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { useCan, useUser } from '@/lib/auth';
import { formatDate, formatMoment } from '@/lib/format';
import { employeeQuery } from '@/lib/queries';
import { EmployeeForm } from './employee-form';

export function EmployeePage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const can = useCan();
  const me = useUser();
  const q = useQuery(employeeQuery(id));
  const [editing, setEditing] = useState(false);

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const e = q.data;

  return (
    <div className="max-w-[980px]">
      <nav aria-label="Отделы" className="mb-4 flex flex-wrap items-center gap-1 text-[13px] text-steel">
        {e.departmentPath.map((name, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: цепочка отделов статична, имена могут повторяться
          <span key={`${name}-${i}`} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="size-3" aria-hidden />}
            {i === e.departmentPath.length - 1 && e.departmentId ? (
              <Link to="/org" search={{ department: e.departmentId }} className="link">
                {name}
              </Link>
            ) : (
              name
            )}
          </span>
        ))}
      </nav>

      <header className="mb-6 flex flex-wrap items-center gap-4">
        <PhotoEditor
          employeeId={e.id}
          name={e.fullName}
          canEdit={me.employee?.id === e.id || can('org.employee.manage')}
        />
        <div className="min-w-0 flex-1">
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.015em]">{e.fullName}</h1>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-steel">
            {e.positionName ?? 'Должность не указана'}
            {e.isDepartmentHead && <Badge tone="warn">руководитель отдела</Badge>}
            {!e.isActive && <Badge tone="danger">уволен</Badge>}
          </p>
        </div>
        {can('org.employee.manage') && !editing && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Изменить
          </Button>
        )}
      </header>

      <div className="grid items-start gap-6 lg:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-6">
          {editing ? (
            <div className="panel">
              <EmployeeForm employee={e} onDone={() => setEditing(false)} />
            </div>
          ) : (
            <section className="panel px-5 py-5">
              <Dl
                items={[
                  ['Отдел', e.departmentName],
                  [
                    'Телефон',
                    e.phone ? (
                      <a key="phone" className="link" href={`tel:${e.phone.replace(/[^\d+]/g, '')}`}>
                        {e.phone}
                      </a>
                    ) : null,
                  ],
                  [
                    'Email',
                    e.email ? (
                      <a key="email" className="link" href={`mailto:${e.email}`}>
                        {e.email}
                      </a>
                    ) : null,
                  ],
                  [
                    'Руководитель',
                    e.manager ? (
                      <Link key="manager" className="link" to="/employees/$id" params={{ id: e.manager.id }}>
                        {e.manager.fullName}
                      </Link>
                    ) : null,
                  ],
                  ['Дата приёма', e.hireDate ? formatDate(e.hireDate) : null],
                  ['Учётка', e.login ?? <span className="text-steel">нет доступа в систему</span>],
                ]}
              />
            </section>
          )}
          <Comments entityType="employee" entityId={e.id} />
        </div>
        {can('audit.read') && <History entityId={e.id} />}
      </div>
    </div>
  );
}

const FIELD_NAMES: Record<string, string> = {
  lastName: 'Фамилия',
  firstName: 'Имя',
  middleName: 'Отчество',
  positionId: 'Должность',
  departmentId: 'Отдел',
  managerId: 'Руководитель',
  email: 'Email',
  phone: 'Телефон',
  hireDate: 'Дата приёма',
  isActive: 'Работает',
  userId: 'Учётка',
};

const ACTIONS: Record<string, string> = {
  created: 'Создан',
  updated: 'Изменён',
  photo_set: 'Фото обновлено',
  photo_removed: 'Фото убрано',
};

function History({ entityId }: { entityId: string }) {
  const q = useQuery({
    queryKey: ['audit', 'employee', entityId],
    queryFn: () => api<AuditItem[]>('/audit', { query: { entityType: 'employee', entityId } }),
    retry: false,
  });
  // Руководителю чужой ветки сервер ответит 403 — блок просто не показываем
  if (q.error) return null;
  return (
    <aside className="panel">
      <h2 className="border-b border-line px-5 py-3.5 font-semibold">История изменений</h2>
      {q.isPending ? (
        <Spinner />
      ) : !q.data?.length ? (
        <p className="px-5 py-4 text-steel">Изменений не было.</p>
      ) : (
        <ol className="divide-y divide-line">
          {q.data.map((a) => (
            <li key={a.id} className="px-5 py-3">
              <p className="text-[13px]">
                <span className="font-medium">{ACTIONS[a.action] ?? a.action}</span>
                <span className="text-steel">, {formatMoment(a.at)}</span>
              </p>
              <p className="text-[12.5px] text-steel">{a.actor?.fullName ?? 'Система'}</p>
              {a.action === 'updated' && a.diff && (
                <ul className="mt-1.5 text-[13px]">
                  {Object.entries(a.diff).map(([field, change]) => (
                    <li key={field}>
                      <span className="text-steel">{FIELD_NAMES[field] ?? field}: </span>
                      {renderValue(change.from)} <span aria-hidden>→</span>
                      <span className="sr-only">на</span> {renderValue(change.to)}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      )}
    </aside>
  );
}

function renderValue(v: unknown) {
  if (v === null || v === undefined || v === '') return <span className="text-steel">пусто</span>;
  if (typeof v === 'boolean') return v ? 'да' : 'нет';
  const s = String(v);
  // Идентификаторы связанных записей показываем коротко — полные имена появятся с журналом v2
  return /^[0-9a-f-]{36}$/.test(s) ? <span className="text-steel">изменено</span> : s;
}

/** Фото в карточке: загрузить (JPG, PNG, WebP до 5 МБ) или убрать. Меняет сам сотрудник или кадровик. */
function PhotoEditor({ employeeId, name, canEdit }: { employeeId: string; name: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const photos = useQuery<EmployeePhoto[]>({ queryKey: ['org', 'photos'], enabled: false });
  const hasPhoto = Boolean(photos.data?.some((p) => p.employeeId === employeeId));
  const save = useMutation({
    mutationFn: async (file: File | null) => {
      let fileId: string | null = null;
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        fileId = (await api<FileItem>('/files', { method: 'POST', body: fd })).id;
      }
      await api(`/org/employees/${employeeId}/photo`, { method: 'PUT', body: { fileId } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org', 'photos'] }),
    onError: (err) => setError(err.message),
  });
  if (!canEdit) return <Avatar name={name} employeeId={employeeId} size={56} />;
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={() => input.current?.click()}
        title="Загрузить фото"
        className="rounded-full focus-visible:outline-2 focus-visible:outline-signal"
      >
        <Avatar name={name} employeeId={employeeId} size={56} />
        <span className="sr-only">Загрузить фото</span>
      </button>
      <input
        ref={input}
        type="file"
        accept={PHOTO_MIMES.join(',')}
        className="hidden"
        onChange={(ev) => {
          const file = ev.target.files?.[0];
          ev.target.value = '';
          setError(null);
          if (!file) return;
          if (file.size > PHOTO_MAX_SIZE) return setError('Фото больше 5 МБ');
          save.mutate(file);
        }}
      />
      <div className="flex gap-2 text-[12px]">
        <button
          type="button"
          className="link"
          onClick={() => input.current?.click()}
          disabled={save.isPending}
        >
          {save.isPending ? 'Загрузка…' : hasPhoto ? 'Сменить' : 'Фото'}
        </button>
        {hasPhoto && (
          <button type="button" className="text-steel hover:text-danger" onClick={() => save.mutate(null)}>
            Убрать
          </button>
        )}
      </div>
      {error && <p className="max-w-[140px] text-center text-[12px] text-danger">{error}</p>}
    </div>
  );
}
